import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { UseModalResult } from 'hooks/useModal';
import { ConfirmDialog } from 'components/common';
import { Typography } from '@mui/material';
import { useDeleteCreativeText } from '../api';
import { useNavigate } from 'react-router';

interface CommonProps {
  id: number;
  name: string;
}

interface DeleteProps
  extends CommonProps,
    Pick<UseModalResult, 'open' | 'close' | 'isOpen'> {
}

const DeleteCreativeText: FC<DeleteProps> = ({ isOpen, close, id, name }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();

  const deleteFlow = useDeleteCreativeText({
    config: {
      onSuccess: () => {
        onShowAlert(`"${name}" deleted`);
        navigate('/cabinet/facebook/creative-texts');
        close();
      },
      onError: () => {
        setErrorMessage('Internal Error');
      },
    },
  });

  const onConfirm = useCallback(() => {
    setErrorMessage(null);
    deleteFlow.mutate({ id });
  }, [deleteFlow, id]);

  return (
    <>
      <ConfirmDialog
        title={`Delete flow ${name}?`}
        text={
          <>
            <Typography variant="body1">
              {name} will be deleted without the possibility of recovery
            </Typography>
            {!!errorMessage && (
              <Typography
                variant="body1"
                sx={(t) => ({ color: t.palette.error.main, pt: 2 })}
              >
                {errorMessage}
              </Typography>
            )}
          </>
        }
        confirmButton="Delete"
        cancelButton="Cancel"
        isOpen={isOpen}
        isPending={deleteFlow.isPending}
        close={() => {
          !deleteFlow.isPending ? close() : null;
        }}
        onCancel={() => {
          !deleteFlow.isPending ? close() : null;
        }}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default DeleteCreativeText;
